import {type ComponentProps, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {type Editor} from '@tiptap/core';
import {LinkNewIcon} from '@time-webkit/all/icons';
import {PopoverOverlay} from '@time-webkit/all/molecules/popover';
import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import noop from '@tinkoff/utils/function/noop';
import {
    dataLinkPopoverTarget,
    dataLinkPreSelectionAttribute,
    LINK_POPOVER_EVENT,
    linkPluginKey,
} from 'packages/editor/src/extensions/link/link';
import {sendToStatist} from 'packages/statist';
import {useEventCallback} from 'usehooks-ts';

import {ShortcutTooltip} from '../../shortcut-tooltip';
import {ActionButton} from '../../action-button';

import {LinkPopoverForm} from '../link-popover-form/link-popover-form';
import {FloatingLinkPopoverForm} from '../floating-link-popover-form/floating-link-popover-form';

type LinkPopoverProps = {
    editor?: Editor;
    disabled?: boolean;
}

const focusScopeProps: ComponentProps<typeof PopoverOverlay>['focusScopeProps'] = {contain: false, restoreFocus: false, autoFocus: true};

export const LinkPopover = ({disabled, editor}: LinkPopoverProps) => {
    const {formatMessage} = useIntl();
    const [show, setShow] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);
    const [decorationNode, setDecorationNode] = useState<Element | null>(null);
    const actionButtonRef = useRef<HTMLButtonElement | null>(null);

    const handlePopoverOpenChange = (isOpen: boolean) => {
        setShow(isOpen);

        if (isOpen) {
            if (!editor?.state.selection) {
                setHasSelection(false);
                setDecorationNode(null);
                return;
            }

            editor.view.dispatch(editor.view.state.tr.setMeta(linkPluginKey, {shouldHighlight: true}));
            setHasSelection(!editor.state.selection.empty);

            const decorationNode = editor.view.dom.querySelector(`span[${dataLinkPopoverTarget}]`);
            if (decorationNode) {
                setDecorationNode(decorationNode);
                return;
            }

            setDecorationNode(editor.view.dom.querySelector(`span[${dataLinkPreSelectionAttribute}]`));
        } else {
            editor?.commands?.focus();
            editor?.view.dispatch(editor.view.state.tr.setMeta(linkPluginKey, {shouldHighlight: false}));
        }
    };

    const handleTogglePopover = useEventCallback(() => handlePopoverOpenChange(!show));

    const handlePress = () => {
        handlePopoverOpenChange(!show);
        sendToStatist('editor.format.tap', {formatType: 'link', hotkey: false});
    };

    const LinkPopoverComponent = hasSelection ? FloatingLinkPopoverForm : LinkPopoverForm;

    useEffect(() => {
        if (!editor) {
            return noop;
        }

        editor.on(LINK_POPOVER_EVENT, handleTogglePopover);

        return () => {
            editor.off(LINK_POPOVER_EVENT, handleTogglePopover);
        };

    // COMMENT: добалять editor в список зависимости не нужно
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownLinkNew} isHidden={disabled}>
                <ActionButton
                    isDisabled={disabled}
                    onPress={handlePress}
                    isActive={show}
                    ref={actionButtonRef}
                    aria-label={formatMessage(KEYBOARD_SHORTCUTS.msgMarkdownLinkNew.label)}
                    icon={<LinkNewIcon />}
                    data-qa='editor_button_link'
                />
            </ShortcutTooltip>
            <PopoverOverlay
                triggerNode={hasSelection ? decorationNode : actionButtonRef.current}
                placement={hasSelection ? 'bottom start' : 'top'}
                isOpen={show}
                offset={4}
                containerPadding={8}
                focusScopeProps={focusScopeProps}
                onOpenChange={handlePopoverOpenChange}
            >
                <LinkPopoverComponent
                    editor={editor}
                    onTogglePopover={handleTogglePopover}
                />
            </PopoverOverlay>
        </>
    );
};
